type TimelineHighlightRect = Pick<DOMRectReadOnly, 'x' | 'y' | 'width' | 'height'>;

export interface TimelineHighlightsGeometrySnapshot {
  readonly hasGroupedRows: boolean;
  readonly isFixedPaneCollapsed: boolean;
  readonly headerHeight: number;
  readonly rowHeight: number;
  readonly contentHeight: number;
  readonly fieldBounds: readonly Pick<TimelineHighlightRect, 'x' | 'width'>[];
  readonly visualRowIndexBySelectionRow: readonly number[];
}

/**
 * Publishes Timeline-owned DOM and immutable geometry to its layout-scoped
 * highlights controller. React owns registration only; bootstrap owns the
 * controller lifecycle.
 */
export interface TimelineHighlightsGeometryAdapter {
  getRoot(): HTMLElement | null;
  getResizeObserveTarget(): HTMLElement | null;
  getSnapshot(): TimelineHighlightsGeometrySnapshot;
  getViewportBounds(): TimelineHighlightRect | null;
}

let currentAdapter: TimelineHighlightsGeometryAdapter | null = null;
const geometryChangeListeners = new Set<
  (adapter: TimelineHighlightsGeometryAdapter | null) => void
>();

function notifyGeometryChangeListeners(): void {
  for (const listener of geometryChangeListeners) {
    listener(currentAdapter);
  }
}

/**
 * Publishes the DOM-backed geometry owned by the mounted Timeline layout.
 * Replacing or removing the active adapter notifies the layout-scoped
 * highlights controller without giving React ownership of that controller.
 */
export function registerTimelineHighlightsGeometry(
  adapter: TimelineHighlightsGeometryAdapter
): () => void {
  currentAdapter = adapter;
  notifyGeometryChangeListeners();

  return () => {
    if (currentAdapter === adapter) {
      currentAdapter = null;
      notifyGeometryChangeListeners();
    }
  };
}

/**
 * Subscribes the Timeline highlights controller to geometry publication.
 * The current adapter is emitted immediately so either bootstrap or React may
 * initialize first. The returned function removes the controller-level listener.
 */
export function subscribeToTimelineHighlightsGeometryChanges(
  listener: (adapter: TimelineHighlightsGeometryAdapter | null) => void
): () => void {
  geometryChangeListeners.add(listener);
  listener(currentAdapter);

  return () => {
    geometryChangeListeners.delete(listener);
  };
}

/**
 * Maps selection-machine indexes onto Timeline's virtual grid without adding
 * Timeline DOM knowledge to the shared highlight renderer. It reads the active
 * adapter at call time so geometry can change without replacing the controller.
 */
export class TimelineHighlightsGeometryResolver {
  getScrollContainer(): HTMLElement | null {
    return currentAdapter?.getRoot() ?? null;
  }

  getCellBounds(rowIndex: number, colIndex: number): TimelineHighlightRect | null {
    const snapshot = currentAdapter?.getSnapshot();
    const viewport = currentAdapter?.getViewportBounds();
    const row = this.#getRowBounds(rowIndex);
    const field = snapshot?.fieldBounds[colIndex];

    if (
      !snapshot ||
      snapshot.hasGroupedRows ||
      snapshot.isFixedPaneCollapsed ||
      !viewport ||
      !row ||
      !field
    ) {
      return null;
    }

    return {
      x: viewport.x + field.x,
      y: viewport.y + row.y,
      width: field.width,
      height: row.height
    };
  }

  getRowBounds(rowIndex: number): TimelineHighlightRect | null {
    if (currentAdapter?.getSnapshot().hasGroupedRows) {
      return null;
    }

    const row = this.#getRowBounds(rowIndex);
    const viewport = currentAdapter?.getViewportBounds();

    return row && viewport
      ? {
          x: viewport.x,
          y: viewport.y + row.y,
          width: viewport.width,
          height: row.height
        }
      : null;
  }

  getColumnBounds(colIndex: number): TimelineHighlightRect | null {
    const snapshot = currentAdapter?.getSnapshot();
    const viewport = currentAdapter?.getViewportBounds();
    const field = snapshot?.fieldBounds[colIndex];

    if (!snapshot || snapshot.isFixedPaneCollapsed || !viewport || !field) {
      return null;
    }

    return {
      x: viewport.x + field.x,
      y: viewport.y + snapshot.headerHeight,
      width: field.width,
      height: Math.max(0, snapshot.contentHeight - snapshot.headerHeight)
    };
  }

  getContainerBounds(): TimelineHighlightRect | null {
    const snapshot = currentAdapter?.getSnapshot();
    const viewport = currentAdapter?.getViewportBounds();

    if (!snapshot || snapshot.hasGroupedRows || !viewport) {
      return null;
    }

    return {
      x: viewport.x,
      y: viewport.y + snapshot.headerHeight,
      width: viewport.width,
      height: Math.max(0, snapshot.contentHeight - snapshot.headerHeight)
    };
  }

  #getRowBounds(rowIndex: number): Pick<TimelineHighlightRect, 'y' | 'height'> | null {
    const snapshot = currentAdapter?.getSnapshot();
    const visualRowIndex = snapshot?.visualRowIndexBySelectionRow[rowIndex];

    if (snapshot === undefined || visualRowIndex === undefined || visualRowIndex < 0) {
      return null;
    }

    return {
      y: snapshot.headerHeight + visualRowIndex * snapshot.rowHeight,
      height: snapshot.rowHeight
    };
  }
}
